import React, { useState } from "react";
import { Copy, Film, Grid3x3, Table2 } from "lucide-react";
import { AccountAvatar, MediaThumb, isStory } from "@/lib/social";

const BADGE = { draft: "Draft", in_review: "In review", with_client: "Pending", rejected: "Changes", rework: "Changes", approved: "Approved", scheduled: "Scheduled" };

export const ViewSwitch = ({ view, onChange, show }) => !show ? null : (
  <div className="inline-flex rounded-full border border-[#EEEFEF] bg-white p-0.5" data-testid="social-view-switch">
    {[["table", "Table", Table2], ["grid", "Instagram grid", Grid3x3]].map(([k, l, I]) => (
      <button key={k} data-testid={`social-view-${k}`} onClick={() => onChange(k)} className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${view === k ? "bg-[#06060C] text-white" : "text-[#595D62] hover:text-[#06060C]"}`}><I className="h-3.5 w-3.5" />{l}</button>
    ))}
  </div>
);

export const igAccounts = (posts, accounts) => (accounts || []).filter((a) => a.network === "instagram" && posts.some((p) => !isStory(p) && p.account_ids?.includes(a.id)));

function Tile({ p, onOpen }) {
  const label = BADGE[p.status];
  const done = p.status === "approved" || p.status === "scheduled";
  return (
    <button data-testid={`ig-grid-tile-${p.id}`} onClick={() => onOpen(p)} className="group relative aspect-[3/4] bg-[#F1F1F4] overflow-hidden">
      {p.media?.length ? <MediaThumb m={p.media[0]} className="h-full w-full transition-transform duration-300 group-hover:scale-[1.03]" />
        : <span className="absolute inset-0 p-3 text-[11px] text-[#595D62] line-clamp-6 text-left">{p.caption || "No image yet"}</span>}
      {p.media?.length > 1 ? <Copy className="absolute top-2 right-2 h-4 w-4 text-white drop-shadow" /> : p.media?.[0]?.kind === "video" && <Film className="absolute top-2 right-2 h-4 w-4 text-white drop-shadow" />}
      {label && <span className={`absolute bottom-2 left-2 rounded-full ${done ? "bg-[#E6F7EE] text-[#1f9d55]" : "bg-white/90 text-[#06060C]"} px-2 py-0.5 text-[10px] font-medium`}>{label}</span>}
    </button>
  );
}

export function InstagramGrid({ posts, accounts, ws, onOpen }) {
  const list = igAccounts(posts, accounts);
  const [sel, setSel] = useState(list[0]?.id);
  const acc = list.find((a) => a.id === sel) || list[0];
  if (!acc) return <p className="text-sm text-[#9FA1A4] py-12 text-center">No Instagram feed posts in this batch.</p>;
  const items = posts.filter((p) => !isStory(p) && p.account_ids?.includes(acc.id))
    .sort((a, b) => (b.scheduled_at || "9999").localeCompare(a.scheduled_at || "9999"));
  return (
    <div className="space-y-4" data-testid="ig-grid">
      {list.length > 1 && (
        <div className="flex flex-wrap gap-1.5">{list.map((a) => <button key={a.id} data-testid={`ig-grid-account-${a.id}`} onClick={() => setSel(a.id)} className={`rounded-full px-3 py-1 text-xs border ${a.id === acc.id ? "bg-[#06060C] text-white border-[#06060C]" : "border-[#EEEFEF] text-[#595D62]"}`}>{(a.handle || a.name).replace(/^@/, "")}</button>)}</div>
      )}
      <div className="max-w-[480px] mx-auto rounded-2xl border border-[#EEEFEF] bg-white overflow-hidden">
        <div className="flex items-center gap-4 px-5 py-5">
          <AccountAvatar a={acc} ws={ws} size={64} />
          <div className="min-w-0">
            <p className="text-[15px] font-semibold text-[#06060C] truncate" data-testid="ig-grid-handle">{(acc.handle || acc.name).replace(/^@/, "")}</p>
            <p className="text-xs text-[#595D62] truncate">{acc.name}</p>
            <p className="text-xs text-[#9FA1A4] mt-1">{items.length} post{items.length === 1 ? "" : "s"} in this batch · newest first</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-0.5 border-t border-[#EEEFEF]">{items.map((p) => <Tile key={p.id} p={p} onOpen={onOpen} />)}</div>
      </div>
      <p className="text-[11px] text-[#9FA1A4] text-center">Shows how this batch will sit on the profile. Tap a post to open it.</p>
    </div>
  );
}
